// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import React, {
  forwardRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import ChevronIcon from "../assets/chevron-left.svg?react";
import * as Styled from "../CaseDetails/CaseDetails.styles";

const ScrollControlsContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollContainer = styled.div<{ isCursorGrab?: boolean }>`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  cursor: ${({ isCursorGrab }) => (isCursorGrab ? "grab" : "default")};
  display: flex;
  user-select: none;
  padding: 0 22px 0 22px;
  margin-right: 40px;
`;

const DEFAULT_SCROLL_OFFSET = 200;

export interface DraggableScrollContainerHandle {
  scrollLeft: () => void;
  scrollRight: () => void;
  scrollTo: (item: number) => void;
}

const DraggableScrollContainer = forwardRef<
  DraggableScrollContainerHandle,
  PropsWithChildren & {
    hideArrowButtons?: boolean;
    scrollOffset?: number;
    onSlideChange?: (slide: number) => void;
  }
>(
  (
    {
      children,
      hideArrowButtons = false,
      scrollOffset = DEFAULT_SCROLL_OFFSET,
      onSlideChange,
    },
    ref,
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);
    const [currentScrollLeft, setCurrentScrollLeft] = useState(0);

    const handleMouseDown = useCallback((e: MouseEvent) => {
      if (scrollRef.current) {
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setDragStartScrollLeft(scrollRef.current.scrollLeft);
      }
    }, []);

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!isDragging || !scrollRef.current) return;
        const xOffset = e.pageX;
        const scrollSpeedDelta = (xOffset - startX) * 0.5;
        setCurrentScrollLeft(dragStartScrollLeft - scrollSpeedDelta);
      },
      [isDragging, dragStartScrollLeft, startX],
    );

    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
    }, []);

    const calculateNewOffset = (currentOffset: number, delta: number) => {
      if (!scrollRef.current) return currentOffset;

      const nextOffset = currentOffset + delta;
      const maxOffset =
        scrollRef.current.scrollWidth - scrollRef.current.offsetWidth;

      return delta < 0
        ? Math.max(nextOffset, 0) // Scrolling left
        : Math.min(nextOffset, maxOffset); // Scrolling right
    };

    const handleScrollLeft = useCallback(() => {
      // calculateNewOffset only reads scrollRef.current (stable) and its own params,
      // so the stale closure reference is safe to exclude.

      setCurrentScrollLeft((prev) => calculateNewOffset(prev, -scrollOffset));
    }, [scrollOffset]);

    const handleScrollRight = useCallback(() => {
      // calculateNewOffset only reads scrollRef.current (stable) and its own params,
      // so the stale closure reference is safe to exclude.

      setCurrentScrollLeft((prev) => calculateNewOffset(prev, scrollOffset));
    }, [scrollOffset]);

    useImperativeHandle(
      ref,
      () => ({
        scrollLeft: handleScrollLeft,
        scrollRight: handleScrollRight,
        scrollTo: (item: number) => setCurrentScrollLeft(item * scrollOffset),
      }),
      [handleScrollLeft, handleScrollRight, scrollOffset],
    );

    useEffect(() => {
      const scrollContainer = scrollRef.current;
      if (scrollContainer) {
        scrollContainer.addEventListener("mousedown", handleMouseDown);
        scrollContainer.addEventListener("mousemove", handleMouseMove);
        scrollContainer.addEventListener("mouseup", handleMouseUp);
        scrollContainer.addEventListener("mouseleave", handleMouseUp);

        return () => {
          scrollContainer.removeEventListener("mousedown", handleMouseDown);
          scrollContainer.removeEventListener("mousemove", handleMouseMove);
          scrollContainer.removeEventListener("mouseup", handleMouseUp);
          scrollContainer.removeEventListener("mouseleave", handleMouseUp);
        };
      }
      return;
    }, [
      isDragging,
      startX,
      dragStartScrollLeft,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
    ]);

    useEffect(() => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollLeft = currentScrollLeft;
    }, [currentScrollLeft]);

    const currentSlide = Math.round(currentScrollLeft / scrollOffset);

    useEffect(() => {
      onSlideChange?.(currentSlide);
    }, [currentSlide, onSlideChange]);

    return (
      <ScrollControlsContainer>
        {!hideArrowButtons && (
          <Styled.CarouselButtons>
            <Styled.CarouselButton onClick={handleScrollLeft}>
              <ChevronIcon />
            </Styled.CarouselButton>
            <Styled.CarouselButton onClick={handleScrollRight}>
              <ChevronIcon />
            </Styled.CarouselButton>
          </Styled.CarouselButtons>
        )}
        <ScrollContainer
          ref={scrollRef}
          isCursorGrab={!hideArrowButtons}
          onScroll={(e) => setCurrentScrollLeft(e.currentTarget.scrollLeft)}
        >
          {children}
        </ScrollContainer>
      </ScrollControlsContainer>
    );
  },
);

DraggableScrollContainer.displayName = "DraggableScrollContainer";

export default DraggableScrollContainer;
